import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/* The LUKEABC print, composed in the browser rather than shipped as a picture
 * of itself — the same decision as the wordmark above it on this page.
 *
 * Same archive, different rule. The wordmark sets the seven letters once, at one
 * height. Here the word runs on and wraps like text, every form scaled to a
 * common row height and butted against its neighbours, so LUKEABC breaks across
 * lines and never lands in the same place twice. Because each form is a found
 * crop carrying its own background, the result reads as a quilt rather than as
 * type on a ground.
 *
 * Forms are dealt from a shuffled deck per letter, not picked at random: every
 * variant in a pool is used before any comes round again. Picking at random put
 * about 40% of the archive on a sheet this size, with the big E and A sets
 * barely touched — the deck fully exhausts L, K, B and C on a single sheet.
 *
 * The layout is computed once in row-height units, so the same arrangement
 * draws to the canvas on the page and to the much larger file the save link
 * writes. What you shuffle to is exactly what you keep.
 */

const WORD = ['L', 'U', 'K', 'E', 'A', 'B', 'C'];
const ROWS = 13;                 // the density that reads as texture but still as letters
const VIEW = { w: 1500, h: 2100 };   // canvas backing, 2x the card for retina
const SAVE = { w: 2250, h: 3150 };   // 750x1050 at 300dpi, tripled

/* Deal a letter's forms like a shuffled deck. */
function deck(items) {
  let rest = [];
  return () => {
    if (!rest.length) {
      rest = items.slice();
      for (let i = rest.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [rest[i], rest[j]] = [rest[j], rest[i]];
      }
    }
    return rest.pop();
  };
}

export default function LetterPrint() {
  const canvasRef = useRef(null);
  const [images, setImages] = useState(null);   // src -> HTMLImageElement
  const [layout, setLayout] = useState(null);
  const [saving, setSaving] = useState(false);

  const pools = useMemo(() => {
    const sets = (typeof window !== 'undefined' && window.LETTERS) || {};
    return WORD.map((ch) => (sets[ch] || []).map((f) => `letters/${ch}/${f}`));
  }, []);

  const ready = pools.every((p) => p.length > 0);

  /* Every form has to be measured before anything can be placed, so load the
     lot up front. They are around 13KB each, and the wordmark on this same page
     has already asked for them, so this is usually just the cache answering. */
  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    const all = pools.flat();
    const map = new Map();
    let left = all.length;
    all.forEach((src) => {
      const img = new Image();
      const done = () => {
        if (cancelled) return;
        if (img.naturalWidth) map.set(src, img);
        left -= 1;
        if (left === 0) setImages(map);
      };
      img.onload = done;
      img.onerror = done;
      img.src = src;
    });
    return () => { cancelled = true; };
  }, [pools, ready]);

  /* Positions in row-height units: x and width vary, every row is 1 tall. */
  const compose = useCallback(() => {
    if (!images) return;
    const decks = pools.map((pool) => deck(pool.filter((s) => images.has(s))));
    const width = ROWS * (VIEW.w / VIEW.h);
    const placed = [];
    let n = 0;
    for (let row = 0; row < ROWS; row += 1) {
      let x = 0;
      while (x < width) {
        const src = decks[n % WORD.length]();
        n += 1;
        const img = images.get(src);
        const w = img.naturalWidth / img.naturalHeight;
        placed.push({ src, x, y: row, w });
        x += w;
      }
    }
    setLayout(placed);
  }, [images, pools]);

  useEffect(() => { compose(); }, [compose]);

  const paint = useCallback((canvas, size) => {
    if (!canvas || !layout || !images) return;
    canvas.width = size.w;
    canvas.height = size.h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, size.w, size.h);
    const unit = size.h / ROWS;
    layout.forEach(({ src, x, y, w }) => {
      const img = images.get(src);
      if (img) ctx.drawImage(img, x * unit, y * unit, w * unit, unit);
    });
  }, [layout, images]);

  useEffect(() => { paint(canvasRef.current, VIEW); }, [paint]);

  const save = useCallback(() => {
    if (!layout || saving) return;
    setSaving(true);
    const off = document.createElement('canvas');
    paint(off, SAVE);
    off.toBlob((blob) => {
      setSaving(false);
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lukeabc-print.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }, 'image/png');
  }, [layout, paint, saving]);

  if (!ready) return null;

  return (
    <div className="main-image letter-print-wrap">
      <canvas
        ref={canvasRef}
        className="letter-print"
        onClick={compose}
        aria-label="LUKEABC composed from the letter archive"
      />
      <div className="caption-text letter-print-hint">
        Click for a new arrangement.{' '}
        <button type="button" className="letter-print-save" onClick={save} disabled={!layout}>
          {saving ? 'Saving…' : 'Save this one'}
        </button>
      </div>
    </div>
  );
}
